import React, { useEffect, useState } from "react";
import { useAuthTagContext } from "../../contexts/TagAndSystemProvider";

/**
 * Interface for auth tag data from API
 */
interface AuthTag {
  id: number;
  name: string;
  is_active?: boolean;
  [key: string]: any;
}

/**
 * Interface for entity tag association objects
 */
interface EntityTagDetail {
  id?: number;
  auth_tag_id: number;
  [key: string]: any;
}

/**
 * Props interface for ViewAuthTags component
 */
interface ViewAuthTagsProps {
  /** Array of tag association objects (e.g., domain_tags_details, resource_tags_details) */
  entityTagsDetails: EntityTagDetail[];
  /** String identifier for the entity type (for toast messages) */
  entityName: string;
  /** Toast function for notifications */
  showToast: (
    message: string,
    type: "success" | "error" | "warning" | "warn"
  ) => void;
  /** Optional CSS class for styling customization */
  className?: string;
  /** Optional loading state callback */
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Reusable component for displaying auth tags in view mode
 * Handles active/inactive filtering and toast notifications for inactive tags
 */
const ViewAuthTags: React.FC<ViewAuthTagsProps> = ({
  entityTagsDetails,
  entityName,
  showToast,
  className = "",
  setIsLoading,
}) => {
  const [inactiveTagsNotified, setInactiveTagsNotified] = useState<Set<number>>(
    new Set()
  );

  // Get all auth tags data from context
  const { allAuthTagsData } = useAuthTagContext();

  // Handle inactive tag notifications
  useEffect(() => {
    if (
      Array.isArray(allAuthTagsData) &&
      Array.isArray(entityTagsDetails) &&
      entityTagsDetails.length > 0
    ) {
      // Find matching auth tags for this entity
      const matchingAuthTags = allAuthTagsData.filter((authTag: AuthTag) =>
        entityTagsDetails.some(
          (entityTag: EntityTagDetail) => entityTag.auth_tag_id === authTag.id
        )
      );

      // Find inactive tags that haven't been notified yet
      const inactiveTags = matchingAuthTags.filter(
        (tag: AuthTag) =>
          tag.is_active === false && !inactiveTagsNotified.has(tag.id)
      );

      // Show toast notifications for new inactive tags
      if (inactiveTags.length > 0) {
        inactiveTags.forEach((tag: AuthTag) => {
          showToast(`Tag ID ${tag.id} is currently inactive`, "error");
        });

        // Update the notified set
        setInactiveTagsNotified((prev) => {
          const newSet = new Set(prev);
          inactiveTags.forEach((tag: AuthTag) => newSet.add(tag.id));
          return newSet;
        });
      }
    }
  }, [
    allAuthTagsData,
    entityTagsDetails,
    entityName,
    inactiveTagsNotified,
    showToast,
  ]);

  // Render the auth tags
  const renderAuthTags = () => {
    if (
      !Array.isArray(allAuthTagsData) ||
      !Array.isArray(entityTagsDetails) ||
      entityTagsDetails.length === 0
    ) {
      return "N/A";
    }

    // Find matching auth tags for this entity
    const matchingAuthTags = allAuthTagsData.filter((authTag: AuthTag) =>
      entityTagsDetails.some(
        (entityTag: EntityTagDetail) => entityTag.auth_tag_id === authTag.id
      )
    );

    // Filter only active tags for display
    const activeTags = matchingAuthTags.filter(
      (tag: AuthTag) => tag.is_active === true || tag.is_active === undefined
    );

    // Return active tags as badges or "N/A" if none
    return activeTags.length > 0
      ? activeTags.map((tag: AuthTag) => tag.name).join(", ")
      : "N/A";
  };

  return (
    <div className={`form-control d-flex flex-wrap ${className}`}>
      {renderAuthTags()}
    </div>
  );
};

export default ViewAuthTags;
